import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Spin, Typography } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getGlobalTermCondition, createGlobalTermCondition, updateGlobalTermCondition } from "../../../../api/termCondition";

const { Title } = Typography;

const TermsConditions = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [termsData, setTermsData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Rich text editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["clean"],
    ],
  };

  // Fetch existing terms and conditions on component mount
  useEffect(() => {
    fetchTermsConditions();
  }, []);

  const fetchTermsConditions = async () => {
    setLoading(true);
    try {
      const data = await getGlobalTermCondition();
      console.log("Fetched terms data:", data);
      if (data) {
        setTermsData(data);
        form.setFieldsValue({
          title: data.title,
          description: data.description,
          termCondition: data.termCondition,
        });
      }
    } catch (error) {
      console.error("Failed to fetch terms and conditions:", error);
      message.error("Failed to load terms and conditions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    console.log("Form values:", values);
    setLoading(true);
    try {
      let data;
      
      if (termsData) {
        // Update existing terms and conditions
        data = await updateGlobalTermCondition(values);
        message.success("Terms and conditions updated successfully");
      } else {
        // Create new terms and conditions
        data = await createGlobalTermCondition(values);
        message.success("Terms and conditions created successfully");
      }
      await fetchTermsConditions();
      // setTermsData(data);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save terms and conditions:", error);
      message.error("Failed to save terms and conditions");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (termsData) {
      form.setFieldsValue({
        title: termsData.title,
        description: termsData.description,
        termCondition: termsData.termCondition,
      });
    } else {
      form.resetFields();
    }
    setIsEditing(false);
  };

  const renderPreview = () => {
    if (!termsData) {
      return (
        <div className="text-center p-8">
          <p>No terms and conditions have been created yet.</p>
          <Button type="primary" onClick={() => setIsEditing(true)}>
            Create Terms & Conditions
          </Button>
        </div>
      );
    }

    return (
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>{termsData.title}</Title>
          <Button type="primary" onClick={handleEdit}>
            Edit
          </Button>
        </div>
        
        {/* Preview that matches customer app UI */}
        <div className="mb-6">
          <Title level={5}>Description</Title>
          <div 
            className="border p-4 rounded-md bg-gray-50 text-[15px] font-light leading-snug"
            dangerouslySetInnerHTML={{ __html: termsData.description }} 
          />
        </div>
        
        <div className="mb-6">
          <Title level={5}>Terms & Conditions</Title>
          <div className="border rounded-3xl bg-white p-6 shadow-sm">
            <div 
              className="text-[15px] font-light leading-snug"
              dangerouslySetInnerHTML={{ __html: termsData.termCondition }} 
            />
          </div>
        </div>
        
        <div className="mt-8">
          <Title level={5}>Preview</Title>
          <div className="border p-2 rounded-lg bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-xl sm:text-4xl font-normal leading-snug text-center text-black mb-6">
                {termsData.title}
              </h1>
              
              <div className="w-full px-3 sm:px-3">
                <div 
                  className="text-[12px] sm:text-[14px] md:text-lg font-light text-center text-black max-w-5xl mx-auto"
                  dangerouslySetInnerHTML={{ __html: termsData.description }}
                />
              </div>
              
              <div className="flex flex-col gap-3 leading-6 text-black md:bg-white rounded-3xl w-full px-3 sm:px-6 pb-6 md:pt-6 md:border border-[#89878E]">
                <div 
                  className="text-[12px] sm:text-[14px] md:text-[15px] leading-snug font-light"
                  dangerouslySetInnerHTML={{ __html: termsData.termCondition }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="p-4"
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="Terms of Service" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          extra="This will appear at the top of the Terms & Conditions page as an introduction."
          rules={[{ required: true, message: "Please enter a description" }]}
        >
          <ReactQuill
            theme="snow"
            modules={modules}
            placeholder="Enter a brief description of the terms and conditions..."
            style={{ minHeight: '200px' }}
          />
        </Form.Item>

        <Form.Item
          name="termCondition"
          label="Terms & Conditions Content"
          extra="This will appear as the main content of the Terms & Conditions page. Use headings to organize sections."
          rules={[{ required: true, message: "Please enter terms and conditions content" }]}
        >
          <ReactQuill
            theme="snow"
            modules={modules}
            placeholder="Enter the full terms and conditions content..."
            style={{ minHeight: '400px' }}
          />
        </Form.Item>

        <Form.Item>
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </div>
        </Form.Item>
      </Form>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {isEditing ? renderForm() : renderPreview()}
    </div>
  );
};

export default TermsConditions;
